import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { animalService, Animal } from '../services/animalService';
import { missingAnimalService } from '../services/missingAnimalService';
import { uploadService } from '../services/uploadService';
import './ReportMissing.css';

const ReportMissing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const canAddAnimal = user && user.role !== 'comum';
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    animal_id: '',
    last_seen_location: '',
    contact_email: '',
    contact_phone: '',
    description: '',
  });

  useEffect(() => {
    loadAnimals();
    // Se houver animal_id na URL, pré-selecionar
    const animalId = searchParams.get('animal_id');
    if (animalId) {
      setFormData(prev => ({ ...prev, animal_id: animalId }));
    }
  }, [searchParams]);

  const loadAnimals = async () => {
    try {
      setLoading(true);
      const data = await animalService.getAnimals();
      setAnimals(data);
    } catch (error: any) {
      console.error('Erro ao carregar animais:', error);
      alert(error.response?.data?.error || 'Erro ao carregar animais');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.animal_id) {
      alert('Selecione um animal');
      return;
    }

    if (!formData.last_seen_location.trim()) {
      alert('Informe onde o animal foi visto pela última vez');
      return;
    }

    if (!formData.contact_email && !formData.contact_phone) {
      alert('É necessário fornecer pelo menos email ou telefone para contato');
      return;
    }

    try {
      setSubmitting(true);

      let imageUrl = '';
      if (selectedFile) {
        const uploadResult = await uploadService.uploadImage(selectedFile);
        imageUrl = uploadResult.image_url;
      }

      await missingAnimalService.createMissingAnimal({
        animal_id: parseInt(formData.animal_id),
        last_seen_location: formData.last_seen_location,
        contact_email: formData.contact_email || undefined,
        contact_phone: formData.contact_phone || undefined,
        image_url: imageUrl || undefined,
        description: formData.description || undefined,
      });

      alert('Animal reportado como desaparecido com sucesso!');
      navigate('/missing-animals');
    } catch (error: any) {
      console.error('Erro ao reportar animal desaparecido:', error);
      alert(error.response?.data?.error || 'Erro ao reportar animal desaparecido');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (animals.length === 0) {
    return (
      <div className="container">
        <div className="card">
          <h2>Nenhum Animal Cadastrado</h2>
          <p>
            {canAddAnimal
              ? 'Você precisa ter pelo menos um animal cadastrado para reportar um desaparecimento.'
              : 'Você precisa ter pelo menos um animal cadastrado para reportar um desaparecimento. O cadastro de animais com microchip é feito por clínicas veterinárias. Procure uma clínica para registrar seu animal.'}
          </p>
          {canAddAnimal && (
            <button onClick={() => navigate('/animals/new')} className="btn btn-primary">
              Cadastrar Animal
            </button>
          )}
          <button onClick={() => navigate('/animals')} className="btn btn-secondary" style={{ marginLeft: canAddAnimal ? 8 : 0 }}>
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Reportar Animal Desaparecido</h1>
        <button onClick={() => navigate('/missing-animals')} className="btn btn-secondary">
          Voltar
        </button>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Animal *</label>
            <select
              value={formData.animal_id}
              onChange={(e) => setFormData({ ...formData, animal_id: e.target.value })}
              className="form-control"
              required
            >
              <option value="">Selecione um animal</option>
              {animals.map((animal) => (
                <option key={animal.id} value={animal.id}>
                  {animal.name} ({animal.species})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Onde o animal foi visto pela última vez? *</label>
            <input
              type="text"
              value={formData.last_seen_location}
              onChange={(e) => setFormData({ ...formData, last_seen_location: e.target.value })}
              className="form-control"
              placeholder="Ex: Rua das Flores, 123 - Centro - São Paulo/SP"
              required
            />
          </div>

          <div className="form-group">
            <label>Email para Contato</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="form-control"
              placeholder="seu@email.com"
            />
            <small>Informe pelo menos email ou telefone</small>
          </div>

          <div className="form-group">
            <label>Telefone para Contato</label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="form-control"
              placeholder="(11) 99999-9999"
            />
            <small>Informe pelo menos email ou telefone</small>
          </div>

          <div className="form-group">
            <label>Foto do Animal (Opcional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="form-control"
            />
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview da foto" />
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    setImagePreview(null);
                  }}
                  className="btn btn-secondary btn-sm image-preview-remove"
                >
                  Remover foto
                </button>
              </div>
            )}
            <small>Formatos aceitos: PNG, JPG, JPEG, GIF, WEBP (máx. 16MB)</small>
          </div>

          <div className="form-group">
            <label>Descrição do Animal (Opcional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="form-control"
              rows={4}
              placeholder="Descreva características físicas, comportamento, ou outras informações que possam ajudar na identificação do animal..."
            />
            <small>
              Se você não tiver uma foto, forneça uma descrição detalhada do animal
            </small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/missing-animals')}
              className="btn btn-secondary"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Enviando...' : 'Reportar Desaparecimento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportMissing;
